import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Button,
  Card,
  CardBody,
  CardHeader,
  Container,
  Dropdown,
  DropdownItem,
  DropdownMenu,
  DropdownToggle,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalHeader,
  Nav,
  NavItem,
  NavLink,
  Offcanvas,
  OffcanvasBody,
  OffcanvasHeader,
  Spinner,
  Table,
} from "reactstrap";
import Flatpickr from "react-flatpickr";
import "flatpickr/dist/plugins/monthSelect/style.css";
import * as XLSX from "xlsx";
import { jsPDF } from "jspdf";
import autoTable from "jspdf-autotable";
import BreadCrumb from "../../../Components/Common/BreadCrumb";
import { requestJson } from "../../../helpers/api";
import baconLogo from "../../../assets/images/baconappen.png";
import { resolveUsuarioActual } from "./ControlStock/formatters";
import { createMonthSelectPlugin } from "./ControlStock/dateUtils";

type ServidoresOfflineResponse = {
  servidores?: string[];
};

type ResumenRecetaRow = {
  tipo: "receta";
  serverOrigen: string;
  descripcion: string | null;
  idReceta: string | null;
  nivelIngrediente: number | null;
  costoTotalSinIva: number;
  costoTotalConIva: number;
  esActiva: boolean | null;
  esProduccion?: boolean | null;
  ingredientesFaltantes: string | null;
  ingredientesReceta: string | null;
  recetaIncompleta: boolean;
  periodosCosto?: string | null;
  familia?: string | null;
};

type DetalleRecetaApiRow = {
  idArticulo: string | null;
  materiaPrima: string | null;
  cantidad: number;
  costoUnitSinIva: number;
  costoUnitConIva: number;
  costoTotalSinIva: number;
  costoTotalConIva: number;
  nivelIngrediente?: number | null;
  origenMesCosto: string | null;
};

type DetalleCompraApiRow = {
  fecha: string | null;
  factura: number | null;
  nroFactura: string | null;
  origenMesCosto?: string | null;
  cantidad: number;
  costoUnitConIva: number;
  importeTotal: number;
  persona: string | null;
  deposito: string | null;
};

type DetalleRecetaModalRow = {
  idArticulo: string | null;
  ingrediente: string;
  cantidad: number;
  costoUnitario: number;
  mesCosto: string;
  subtotal: number;
  nivelIngrediente: number | null;
};

type ComponenteDetalleTarget = {
  idArticulo: string;
  ingrediente: string;
  tipo: "compra" | "receta";
};

const costFormat = new Intl.NumberFormat("es-PY", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 0,
});

const quantityFormat = new Intl.NumberFormat("es-PY", {
  minimumFractionDigits: 0,
  maximumFractionDigits: 4,
});

const getCurrentMonth = () => {
  const now = new Date();
  const year = now.getFullYear();
  const month = String(now.getMonth() + 1).padStart(2, "0");
  return `${year}-${month}`;
};

const parseMonthForPicker = (value?: string | null) => {
  if (!value || !/^\d{4}-\d{2}$/.test(value)) {
    return null;
  }
  const parsed = new Date(`${value}-01T00:00:00`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatPeriodoMes = (value: string) => {
  const parsed = parseMonthForPicker(value);
  if (!parsed) return value || "-";
  const label = new Intl.DateTimeFormat("es-PY", {
    month: "long",
    year: "numeric",
  }).format(parsed);
  return label.charAt(0).toUpperCase() + label.slice(1);
};

const StockRecetasOfflinePage = () => {
  const [serverOptions, setServerOptions] = useState<string[]>([]);
  const [serverSeleccionado, setServerSeleccionado] = useState("");
  const [periodo, setPeriodo] = useState(getCurrentMonth());
  const [modoCosto, setModoCosto] = useState<"ultimo" | "ponderado">("ponderado");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [rows, setRows] = useState<ResumenRecetaRow[]>([]);
  const [excelDropdownOpen, setExcelDropdownOpen] = useState(false);
  const [pdfDropdownOpen, setPdfDropdownOpen] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [logoDataUrl, setLogoDataUrl] = useState<string | null>(null);
  const [detailsModalOpen, setDetailsModalOpen] = useState(false);
  const [selectedRow, setSelectedRow] = useState<ResumenRecetaRow | null>(null);
  const [activeFamily, setActiveFamily] = useState<string>("");
  const [estadoFiltro, setEstadoFiltro] = useState<"todos" | "activo" | "inactivo">("todos");
  const [produccionFiltro, setProduccionFiltro] = useState<"todos" | "si" | "no">("todos");
  const [detailRows, setDetailRows] = useState<DetalleRecetaModalRow[]>([]);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [detailsError, setDetailsError] = useState<string | null>(null);
  const [componentModalOpen, setComponentModalOpen] = useState(false);
  const [componentTarget, setComponentTarget] = useState<ComponenteDetalleTarget | null>(null);
  const [loadingComponent, setLoadingComponent] = useState(false);
  const [componentError, setComponentError] = useState<string | null>(null);
  const [componentCompraRows, setComponentCompraRows] = useState<DetalleCompraApiRow[]>([]);
  const [componentRecetaRows, setComponentRecetaRows] = useState<DetalleRecetaApiRow[]>([]);
  const [subrecipeCompraModalOpen, setSubrecipeCompraModalOpen] = useState(false);
  const [subrecipeCompraTarget, setSubrecipeCompraTarget] = useState<{
    idArticulo: string;
    ingrediente: string;
  } | null>(null);
  const [subrecipeCompraRows, setSubrecipeCompraRows] = useState<DetalleCompraApiRow[]>([]);
  const [subrecipeCompraError, setSubrecipeCompraError] = useState<string | null>(null);
  const [loadingSubrecipeCompra, setLoadingSubrecipeCompra] = useState(false);

  useEffect(() => {
    document.title = "Stock - Recetas (Consolidado) | Informes | Saas";
  }, []);

  const loadServers = useCallback(async () => {
    try {
      const response = await requestJson<ServidoresOfflineResponse>(
        "/api/informes/ventas/comparativo-offline/filtros",
      );
      const options = Array.from(
        new Set(
          (response?.servidores ?? [])
            .map((item) => item.trim().toUpperCase())
            .filter((item) => item.length > 0),
        ),
      );
      setServerOptions(options);
      setServerSeleccionado((prev) => {
        if (prev && options.includes(prev)) return prev;
        if (options.includes("PARLA_SERVER")) return "PARLA_SERVER";
        return options[0] ?? "";
      });
    } catch (err) {
      console.error("No se pudieron cargar los servidores:", err);
      setServerOptions([]);
      setServerSeleccionado("");
      setError("No se pudieron cargar los servidores.");
    }
  }, []);

  useEffect(() => {
    void loadServers();
  }, [loadServers]);

  useEffect(() => {
    let active = true;
    const loadLogo = async () => {
      try {
        const sources = [baconLogo, "/baconappen_529x124.png", "/baconappen.png"];
        for (const source of sources) {
          if (!source) continue;
          try {
            const response = await fetch(source);
            if (!response.ok) continue;
            const blob = await response.blob();
            const reader = new FileReader();
            const dataUrl = await new Promise<string>((resolve, reject) => {
              reader.onloadend = () => {
                if (typeof reader.result === "string") {
                  resolve(reader.result);
                } else {
                  reject(new Error("No pudimos leer el logo."));
                }
              };
              reader.onerror = () => reject(new Error("No pudimos leer el logo."));
              reader.readAsDataURL(blob);
            });
            if (active) {
              setLogoDataUrl(dataUrl);
            }
            return;
          } catch {
            continue;
          }
        }
      } catch {
        // noop
      }
    };
    void loadLogo();
    return () => {
      active = false;
    };
  }, []);

  useEffect(() => {
    if (!serverSeleccionado || !periodo) {
      setRows([]);
      return;
    }

    let active = true;
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        const params = new URLSearchParams({
          periodo,
          server: serverSeleccionado,
          tipo: "receta",
          modoCosto,
        });
        const data = await requestJson<ResumenRecetaRow[]>(
          `/api/informes/recetas/resumen?${params.toString()}`,
        );
        if (!active) return;
        setRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!active) return;
        console.error("No se pudo cargar el informe de recetas:", err);
        setError(
          err instanceof Error
            ? err.message
            : "No se pudo cargar el informe de recetas.",
        );
        setRows([]);
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    };
    void loadData();

    return () => {
      active = false;
    };
  }, [modoCosto, periodo, serverSeleccionado]);

  const rowFamilyLabel = useCallback((row: ResumenRecetaRow) => {
    const familia = String(row.familia ?? "").trim();
    return familia || "Sin familia";
  }, []);

  const rowsByFilters = useMemo(() => {
    return rows.filter((row) => {
      if (estadoFiltro === "activo" && row.esActiva !== true) return false;
      if (estadoFiltro === "inactivo" && row.esActiva !== false) return false;
      if (produccionFiltro === "si" && row.esProduccion !== true) return false;
      if (produccionFiltro === "no" && row.esProduccion !== false) return false;
      return true;
    });
  }, [estadoFiltro, produccionFiltro, rows]);

  const familyTabs = useMemo(() => {
    const counts = new Map<string, number>();
    rowsByFilters.forEach((row) => {
      const familia = rowFamilyLabel(row);
      counts.set(familia, (counts.get(familia) ?? 0) + 1);
    });
    return Array.from(counts.entries())
      .sort((a, b) => a[0].localeCompare(b[0], "es", { sensitivity: "base" }))
      .map(([name, count]) => ({ name, count }));
  }, [rowFamilyLabel, rowsByFilters]);

  useEffect(() => {
    setActiveFamily((prev) => {
      if (prev && familyTabs.some((tab) => tab.name === prev)) return prev;
      return familyTabs[0]?.name ?? "";
    });
  }, [familyTabs]);

  const visibleRows = useMemo(() => {
    if (!activeFamily) return rowsByFilters;
    return rowsByFilters.filter((row) => rowFamilyLabel(row) === activeFamily);
  }, [activeFamily, rowFamilyLabel, rowsByFilters]);

  const totalCostoConIva = useMemo(
    () => visibleRows.reduce((acc, row) => acc + Number(row.costoTotalConIva ?? 0), 0),
    [visibleRows],
  );

  const openDetailsModal = useCallback((row: ResumenRecetaRow) => {
    setSelectedRow(row);
    setDetailsModalOpen(true);
  }, []);

  const closeDetailsModal = useCallback(() => {
    setDetailsModalOpen(false);
    setSelectedRow(null);
    setDetailRows([]);
    setDetailsError(null);
    setLoadingDetails(false);
    setComponentModalOpen(false);
    setComponentTarget(null);
    setComponentError(null);
    setLoadingComponent(false);
    setComponentCompraRows([]);
    setComponentRecetaRows([]);
    setSubrecipeCompraModalOpen(false);
    setSubrecipeCompraTarget(null);
    setSubrecipeCompraRows([]);
    setSubrecipeCompraError(null);
    setLoadingSubrecipeCompra(false);
  }, []);

  const openComponentModal = useCallback((row: DetalleRecetaModalRow) => {
    if (!row.idArticulo) return;
    setComponentTarget({
      idArticulo: row.idArticulo,
      ingrediente: row.ingrediente,
      tipo: row.nivelIngrediente === 2 ? "receta" : "compra",
    });
    setComponentModalOpen(true);
  }, []);

  const closeComponentModal = useCallback(() => {
    setComponentModalOpen(false);
    setComponentTarget(null);
    setComponentError(null);
    setLoadingComponent(false);
    setComponentCompraRows([]);
    setComponentRecetaRows([]);
    setSubrecipeCompraModalOpen(false);
    setSubrecipeCompraTarget(null);
    setSubrecipeCompraRows([]);
    setSubrecipeCompraError(null);
    setLoadingSubrecipeCompra(false);
  }, []);

  const openSubrecipeCompraModal = useCallback(
    (idArticulo: string | null, ingrediente: string | null) => {
      if (!idArticulo) return;
      setSubrecipeCompraTarget({
        idArticulo,
        ingrediente: ingrediente ?? "Sin ingrediente",
      });
      setSubrecipeCompraModalOpen(true);
    },
    [],
  );

  const closeSubrecipeCompraModal = useCallback(() => {
    setSubrecipeCompraModalOpen(false);
    setSubrecipeCompraTarget(null);
    setSubrecipeCompraRows([]);
    setSubrecipeCompraError(null);
    setLoadingSubrecipeCompra(false);
  }, []);

  useEffect(() => {
    if (!detailsModalOpen || !selectedRow) {
      return;
    }

    if (!selectedRow.idReceta) {
      setDetailRows([]);
      setDetailsError("La receta no tiene ID para consultar el detalle.");
      setLoadingDetails(false);
      return;
    }

    let active = true;
    const loadDetalle = async () => {
      setLoadingDetails(true);
      setDetailsError(null);
      try {
        const params = new URLSearchParams({
          periodo,
          server: serverSeleccionado,
          idReceta: selectedRow.idReceta ?? "",
          modoCosto,
        });
        const data = await requestJson<DetalleRecetaApiRow[]>(
          `/api/informes/recetas/detalle?${params.toString()}`,
        );
        if (!active) return;
        const nextRows = (Array.isArray(data) ? data : []).map((row) => ({
          idArticulo: row.idArticulo ?? null,
          ingrediente: row.materiaPrima ?? "Sin ingrediente",
          cantidad: Number(row.cantidad ?? 0),
          costoUnitario: Number(row.costoUnitConIva ?? 0),
          mesCosto: row.origenMesCosto || periodo,
          subtotal: Number(row.costoTotalConIva ?? 0),
          nivelIngrediente:
            typeof row.nivelIngrediente === "number" ? row.nivelIngrediente : null,
        }));
        setDetailRows(nextRows);
      } catch (err) {
        if (!active) return;
        console.error("No se pudo cargar el detalle de receta:", err);
        setDetailsError(
          err instanceof Error
            ? err.message
            : "No se pudo cargar el detalle de receta.",
        );
        setDetailRows([]);
      } finally {
        if (active) {
          setLoadingDetails(false);
        }
      }
    };

    void loadDetalle();
    return () => {
      active = false;
    };
  }, [detailsModalOpen, modoCosto, periodo, selectedRow, serverSeleccionado]);

  useEffect(() => {
    if (!componentModalOpen || !componentTarget) {
      return;
    }

    let active = true;
    const loadComponente = async () => {
      setLoadingComponent(true);
      setComponentError(null);
      setComponentCompraRows([]);
      setComponentRecetaRows([]);

      try {
        if (componentTarget.tipo === "receta") {
          const params = new URLSearchParams({
            periodo,
            server: serverSeleccionado,
            idReceta: componentTarget.idArticulo,
            modoCosto,
          });
          const data = await requestJson<DetalleRecetaApiRow[]>(
            `/api/informes/recetas/detalle?${params.toString()}`,
          );
          if (!active) return;
          setComponentRecetaRows(Array.isArray(data) ? data : []);
          return;
        }

        const params = new URLSearchParams({
          periodo,
          server: serverSeleccionado,
          idArticulo: componentTarget.idArticulo,
        });
        const data = await requestJson<DetalleCompraApiRow[]>(
          `/api/informes/compras/detalle?${params.toString()}`,
        );
        if (!active) return;
        setComponentCompraRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!active) return;
        console.error("No se pudo cargar el detalle del componente:", err);
        setComponentError(
          err instanceof Error
            ? err.message
            : "No se pudo cargar el detalle del componente.",
        );
      } finally {
        if (active) {
          setLoadingComponent(false);
        }
      }
    };

    void loadComponente();
    return () => {
      active = false;
    };
  }, [componentModalOpen, componentTarget, modoCosto, periodo, serverSeleccionado]);

  useEffect(() => {
    if (!subrecipeCompraModalOpen || !subrecipeCompraTarget) {
      return;
    }

    let active = true;
    const loadSubrecipeCompras = async () => {
      setLoadingSubrecipeCompra(true);
      setSubrecipeCompraError(null);
      setSubrecipeCompraRows([]);
      try {
        const params = new URLSearchParams({
          periodo,
          server: serverSeleccionado,
          idArticulo: subrecipeCompraTarget.idArticulo,
        });
        const data = await requestJson<DetalleCompraApiRow[]>(
          `/api/informes/compras/detalle?${params.toString()}`,
        );
        if (!active) return;
        setSubrecipeCompraRows(Array.isArray(data) ? data : []);
      } catch (err) {
        if (!active) return;
        console.error("No se pudo cargar compras de subreceta:", err);
        setSubrecipeCompraError(
          err instanceof Error
            ? err.message
            : "No se pudo cargar el detalle de compras.",
        );
      } finally {
        if (active) {
          setLoadingSubrecipeCompra(false);
        }
      }
    };

    void loadSubrecipeCompras();
    return () => {
      active = false;
    };
  }, [periodo, serverSeleccionado, subrecipeCompraModalOpen, subrecipeCompraTarget]);

  const detailTotalConIva = useMemo(
    () => detailRows.reduce((acc, row) => acc + Number(row.subtotal ?? 0), 0),
    [detailRows],
  );
  const detailTotalCantidad = useMemo(
    () => detailRows.reduce((acc, row) => acc + Number(row.cantidad ?? 0), 0),
    [detailRows],
  );
  const detailCostoUnitPromedioPonderado = useMemo(() => {
    if (detailTotalCantidad <= 0) return null;
    return detailTotalConIva / detailTotalCantidad;
  }, [detailTotalCantidad, detailTotalConIva]);

  const componentRecetaTotalCantidad = useMemo(
    () => componentRecetaRows.reduce((acc, row) => acc + Number(row.cantidad ?? 0), 0),
    [componentRecetaRows],
  );
  const componentRecetaTotalConIva = useMemo(
    () =>
      componentRecetaRows.reduce(
        (acc, row) => acc + Number(row.costoTotalConIva ?? 0),
        0,
      ),
    [componentRecetaRows],
  );
  const componentRecetaCostoUnitPromedioPonderado = useMemo(() => {
    if (componentRecetaTotalCantidad <= 0) return null;
    return componentRecetaTotalConIva / componentRecetaTotalCantidad;
  }, [componentRecetaTotalCantidad, componentRecetaTotalConIva]);

  const componentCompraTotalCantidad = useMemo(
    () => componentCompraRows.reduce((acc, row) => acc + Number(row.cantidad ?? 0), 0),
    [componentCompraRows],
  );
  const componentCompraMesCosto = useMemo(
    () =>
      componentCompraRows.find((row) => row.origenMesCosto)?.origenMesCosto ?? null,
    [componentCompraRows],
  );
  const componentCompraTotalImporte = useMemo(
    () =>
      componentCompraRows.reduce((acc, row) => acc + Number(row.importeTotal ?? 0), 0),
    [componentCompraRows],
  );
  const componentCompraCostoUnitPromedioPonderado = useMemo(() => {
    if (componentCompraTotalCantidad <= 0) return null;
    return componentCompraTotalImporte / componentCompraTotalCantidad;
  }, [componentCompraTotalCantidad, componentCompraTotalImporte]);

  const subrecipeCompraTotalCantidad = useMemo(
    () => subrecipeCompraRows.reduce((acc, row) => acc + Number(row.cantidad ?? 0), 0),
    [subrecipeCompraRows],
  );
  const subrecipeCompraMesCosto = useMemo(
    () =>
      subrecipeCompraRows.find((row) => row.origenMesCosto)?.origenMesCosto ?? null,
    [subrecipeCompraRows],
  );
  const subrecipeCompraTotalImporte = useMemo(
    () =>
      subrecipeCompraRows.reduce((acc, row) => acc + Number(row.importeTotal ?? 0), 0),
    [subrecipeCompraRows],
  );
  const subrecipeCompraCostoUnitPromedioPonderado = useMemo(() => {
    if (subrecipeCompraTotalCantidad <= 0) return null;
    return subrecipeCompraTotalImporte / subrecipeCompraTotalCantidad;
  }, [subrecipeCompraTotalCantidad, subrecipeCompraTotalImporte]);

  const exportDisabled = loading || rowsByFilters.length === 0;
  type ExportTarget = string | "all";

  const getExportRows = useCallback(
    (target: ExportTarget) => {
      if (target === "all") {
        return {
          rows: rowsByFilters,
          familyLabel: "Todas",
        };
      }
      return {
        rows: rowsByFilters.filter((row) => rowFamilyLabel(row) === target),
        familyLabel: target,
      };
    },
    [rowFamilyLabel, rowsByFilters],
  );

  const toFileSafe = useCallback((value: string) => {
    return value
      .normalize("NFD")
      .replace(/[\u0300-\u036f]/g, "")
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-+|-+$/g, "");
  }, []);

  const handleExportExcel = useCallback((target: ExportTarget = "all") => {
    if (exportDisabled) return;

    const { rows: exportRows, familyLabel } = getExportRows(target);
    if (exportRows.length === 0) return;

    const workbook = XLSX.utils.book_new();
    const dataRows = exportRows.map((row) => ({
      ID_RECETA: row.idReceta ?? "",
      RECETA: row.descripcion ?? "",
      FAMILIA: rowFamilyLabel(row),
      NIVEL: row.nivelIngrediente ?? "",
      COSTO_SIN_IVA: Number(row.costoTotalSinIva ?? 0),
      COSTO_CON_IVA: Number(row.costoTotalConIva ?? 0),
      PERIODO_COSTO: row.periodosCosto ?? periodo,
      ESTADO: row.esActiva ? "Activa" : "Inactiva",
      SERVIDOR: serverSeleccionado || "",
      PERIODO: periodo,
      MODO_COSTO: modoCosto,
    }));
    const totalCostoConIvaExport = exportRows.reduce(
      (acc, row) => acc + Number(row.costoTotalConIva ?? 0),
      0,
    );
    dataRows.push({
      ID_RECETA: "",
      RECETA: "TOTAL",
      FAMILIA: "",
      NIVEL: "",
      COSTO_SIN_IVA: 0,
      COSTO_CON_IVA: Number(totalCostoConIvaExport ?? 0),
      PERIODO_COSTO: periodo,
      ESTADO: "",
      SERVIDOR: serverSeleccionado || "",
      PERIODO: periodo,
      MODO_COSTO: modoCosto,
    });

    const sheet = XLSX.utils.json_to_sheet(dataRows);
    XLSX.utils.book_append_sheet(workbook, sheet, "Recetas");

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const targetLabel = target === "all" ? "todas" : familyLabel;
    XLSX.writeFile(
      workbook,
      `stock-recetas-${toFileSafe(serverSeleccionado || "server")}-${toFileSafe(periodo)}-${toFileSafe(modoCosto)}-${toFileSafe(targetLabel)}-${timestamp}.xlsx`,
    );
  }, [
    exportDisabled,
    getExportRows,
    modoCosto,
    periodo,
    rowFamilyLabel,
    serverSeleccionado,
    toFileSafe,
  ]);

  const handleExportPdf = useCallback((target: ExportTarget = "all") => {
    if (exportDisabled) return;

    const { rows: exportRows, familyLabel } = getExportRows(target);
    if (exportRows.length === 0) return;

    const doc = new jsPDF({
      orientation: "landscape",
      unit: "pt",
      format: "a4",
    });

    const leftX = 40;
    const rightX = 20;
    const pageWidth = doc.internal.pageSize.getWidth();
    const infoRightX = pageWidth - rightX;
    const logoWidth = 110;
    const logoHeight = 24;
    const headerTopY = 24;
    const titleY = logoDataUrl ? headerTopY + logoHeight + 10 : headerTopY + 16;
    const infoStartY = headerTopY + 4;
    const infoRowHeight = 11;
    const infoLines = [
      { label: "Servidor", value: serverSeleccionado || "-" },
      { label: "Período", value: periodo },
      { label: "Familia", value: familyLabel || "-" },
      { label: "Modo costo", value: modoCosto },
      { label: "Registros", value: String(exportRows.length) },
    ];
    const infoBottomY = infoStartY + (infoLines.length - 1) * infoRowHeight + 2;
    const titleBottomY = titleY + 35;
    const headerHeight = Math.max(infoBottomY, titleBottomY) + 10;

    const fechaHora = new Date().toLocaleString("es-PY");
    const usuario = resolveUsuarioActual();

    const renderHeader = () => {
      if (logoDataUrl) {
        doc.addImage(logoDataUrl, "PNG", leftX, headerTopY - 4, logoWidth, logoHeight);
      }
      doc.setTextColor(0, 0, 0);
      doc.setFontSize(14);
      doc.text("Stock - Recetas", leftX, titleY);
      doc.setFontSize(8);
      doc.setTextColor(0, 0, 0);
      doc.text("Generado: ", leftX, titleY + 13);
      doc.setTextColor(120, 120, 120);
      doc.text(fechaHora, leftX + doc.getTextWidth("Generado: "), titleY + 13);
      doc.setTextColor(0, 0, 0);
      doc.text("Usuario: ", leftX, titleY + 24);
      doc.setTextColor(120, 120, 120);
      doc.text(usuario, leftX + doc.getTextWidth("Usuario: "), titleY + 24);

      infoLines.forEach((line, index) => {
        const y = infoStartY + index * infoRowHeight;
        const label = `${line.label}: `;
        const labelWidth = doc.getTextWidth(label);
        const valueWidth = doc.getTextWidth(line.value);
        const startX = infoRightX - (labelWidth + valueWidth);
        doc.setTextColor(0, 0, 0);
        doc.text(label, startX, y);
        doc.setTextColor(120, 120, 120);
        doc.text(line.value, startX + labelWidth, y);
      });
      doc.setTextColor(0, 0, 0);
    };

    const body = exportRows.map((row) => [
      `${row.idReceta ?? "-"} · ${row.descripcion ?? "Sin descripción"}`,
      String(row.nivelIngrediente ?? "-"),
      costFormat.format(Number(row.costoTotalSinIva ?? 0)),
      costFormat.format(Number(row.costoTotalConIva ?? 0)),
      row.periodosCosto ?? periodo,
      row.esActiva ? "Activa" : "Inactiva",
    ]);
    const totalCostoConIvaExport = exportRows.reduce(
      (acc, row) => acc + Number(row.costoTotalConIva ?? 0),
      0,
    );
    body.push([
      { content: "TOTAL", styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: "-", styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: "-", styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: costFormat.format(Number(totalCostoConIvaExport ?? 0)), styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: periodo, styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
      { content: "", styles: { fontStyle: "bold", fillColor: [33, 37, 41], textColor: [255, 255, 255] } },
    ] as any);

    autoTable(doc, {
      head: [["Receta", "Nivel", "Costo sin IVA", "Costo con IVA", "Período costo", "Estado"]],
      body,
      startY: headerHeight + 16,
      margin: { left: leftX, right: rightX, top: headerHeight },
      theme: "grid",
      styles: { fontSize: 7, cellPadding: 3 },
      headStyles: { fillColor: [33, 37, 41] },
      columnStyles: {
        0: { halign: "left" },
        1: { halign: "right" },
        2: { halign: "right" },
        3: { halign: "right" },
        4: { halign: "left" },
        5: { halign: "left" },
      },
      didDrawPage: () => {
        renderHeader();
      },
    });

    const timestamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");
    const targetLabel = target === "all" ? "todas" : familyLabel;
    doc.save(
      `stock-recetas-${toFileSafe(serverSeleccionado || "server")}-${toFileSafe(periodo)}-${toFileSafe(modoCosto)}-${toFileSafe(targetLabel)}-${timestamp}.pdf`,
    );
  }, [
    exportDisabled,
    getExportRows,
    logoDataUrl,
    modoCosto,
    periodo,
    serverSeleccionado,
    toFileSafe,
  ]);

  return (
    <div className="page-content">
      <Container fluid className="px-2 px-md-3">
        <BreadCrumb title="Stock - Recetas" pageTitle="Informes" />
        <Card className="border-0 shadow-sm">
          <CardHeader className="bg-transparent">
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
              <div>
                <h5 className="mb-1">Recetas</h5>
                <p className="text-muted mb-0">
                  Datos consolidados de recetas y costos por receta.
                </p>
              </div>
              <div className="d-flex align-items-center gap-2">
                <Dropdown
                  isOpen={excelDropdownOpen}
                  toggle={() => setExcelDropdownOpen((prev) => !prev)}
                >
                  <DropdownToggle
                    color="dark"
                    className="btn-soft-dark"
                    caret
                    disabled={exportDisabled}
                  >
                    <i className="ri-file-excel-2-line me-1"></i>
                    Excel
                  </DropdownToggle>
                  <DropdownMenu end>
                    {familyTabs.map((tab) => (
                      <DropdownItem key={`excel-${tab.name}`} onClick={() => handleExportExcel(tab.name)}>
                        <i className="ri-file-list-3-line me-2"></i>
                        {tab.name} ({tab.count})
                      </DropdownItem>
                    ))}
                    {familyTabs.length > 0 ? <DropdownItem divider /> : null}
                    <DropdownItem onClick={() => handleExportExcel("all")}>
                      <i className="ri-file-copy-2-line me-2"></i>
                      Todas
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <Dropdown
                  isOpen={pdfDropdownOpen}
                  toggle={() => setPdfDropdownOpen((prev) => !prev)}
                >
                  <DropdownToggle
                    color="dark"
                    className="btn-soft-dark"
                    caret
                    disabled={exportDisabled}
                  >
                    <i className="ri-file-pdf-2-line me-1"></i>
                    PDF
                  </DropdownToggle>
                  <DropdownMenu end>
                    {familyTabs.map((tab) => (
                      <DropdownItem key={`pdf-${tab.name}`} onClick={() => handleExportPdf(tab.name)}>
                        <i className="ri-file-list-3-line me-2"></i>
                        {tab.name} ({tab.count})
                      </DropdownItem>
                    ))}
                    {familyTabs.length > 0 ? <DropdownItem divider /> : null}
                    <DropdownItem onClick={() => handleExportPdf("all")}>
                      <i className="ri-file-copy-2-line me-2"></i>
                      Todas
                    </DropdownItem>
                  </DropdownMenu>
                </Dropdown>
                <Button
                  color="dark"
                  className="btn-soft-dark"
                  onClick={() => setFiltersOpen(true)}
                >
                  <i className="ri-equalizer-line me-1"></i>
                  Filtros
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-3">
              <div className="text-muted small">
                Servidor: {serverSeleccionado || "-"} · Período:{" "}
                {formatPeriodoMes(periodo)} · Modo costo:{" "}
                {modoCosto === "ponderado" ? "Ponderado" : "Último"} · Registros:{" "}
                {visibleRows.length}/{rowsByFilters.length}
              </div>
            </div>

            {error ? <Alert color="danger">{error}</Alert> : null}
            {loading ? (
              <div className="text-muted py-4">
                <Spinner size="sm" className="me-2" />
                Cargando recetas...
              </div>
            ) : (
              <>
                {familyTabs.length > 0 ? (
                  <Nav pills className="mb-3 flex-wrap gap-2">
                    {familyTabs.map((tab) => (
                      <NavItem key={tab.name}>
                        <NavLink
                          href="#"
                          active={activeFamily === tab.name}
                          onClick={(event) => {
                            event.preventDefault();
                            setActiveFamily(tab.name);
                          }}
                          className="py-1 px-2"
                        >
                          {tab.name}{" "}
                          <span className="text-muted">({tab.count})</span>
                        </NavLink>
                      </NavItem>
                    ))}
                  </Nav>
                ) : null}

                <div className="table-responsive">
                  <Table hover className="align-middle mb-0 table-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th>Receta</th>
                        <th className="text-end">Nivel</th>
                        <th className="text-end">Costo sin IVA</th>
                        <th className="text-end">Costo con IVA</th>
                        <th>Período costo</th>
                        <th>Estado</th>
                        <th className="text-center">ACC.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {visibleRows.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-center text-muted py-4">
                            Sin datos para los filtros seleccionados.
                          </td>
                        </tr>
                      ) : (
                        visibleRows.map((row, index) => (
                          <tr key={`${row.idReceta ?? "sin-id"}-${index}`}>
                            <td>
                              <div className="d-flex align-items-center gap-2 text-nowrap">
                                <span className="badge bg-primary-subtle text-primary">
                                  {row.idReceta ?? "SIN ID"}
                                </span>
                                <span
                                  className="fw-semibold text-truncate"
                                  style={{ maxWidth: 420 }}
                                  title={row.descripcion ?? "Sin descripción"}
                                >
                                  {row.descripcion ?? "Sin descripción"}
                                </span>
                              </div>
                            </td>
                            <td className="text-end">
                              {row.nivelIngrediente ?? "-"}
                            </td>
                            <td className="text-end">
                              {costFormat.format(Number(row.costoTotalSinIva ?? 0))}
                            </td>
                            <td className="text-end">
                              {costFormat.format(Number(row.costoTotalConIva ?? 0))}
                            </td>
                            <td>{row.periodosCosto ?? periodo}</td>
                            <td>
                              <span
                                className={`badge ${
                                  row.esActiva
                                    ? "bg-success-subtle text-success"
                                    : "bg-secondary-subtle text-secondary"
                                }`}
                              >
                                {row.esActiva ? "Activa" : "Inactiva"}
                              </span>
                            </td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-soft-primary"
                                  title="Ver composición"
                                  onClick={() => openDetailsModal(row)}
                                >
                                  <i className="ri-search-eye-line"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="table-light">
                        <th>Total</th>
                        <th className="text-end">-</th>
                        <th className="text-end">-</th>
                        <th className="text-end">{costFormat.format(totalCostoConIva)}</th>
                        <th>{periodo}</th>
                        <th />
                        <th className="text-center text-muted">-</th>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              </>
            )}
          </CardBody>
        </Card>
      </Container>

      <Offcanvas
        isOpen={filtersOpen}
        toggle={() => setFiltersOpen(false)}
        direction="end"
        className="border-start"
      >
        <OffcanvasHeader toggle={() => setFiltersOpen(false)}>
          Filtros
        </OffcanvasHeader>
        <OffcanvasBody>
          <FormGroup className="mb-3">
            <Label className="form-label mb-1">Servidor</Label>
            <Input
              type="select"
              value={serverSeleccionado}
              onChange={(event) => setServerSeleccionado(event.target.value)}
            >
              {serverOptions.length === 0 ? (
                <option value="">Sin servidores</option>
              ) : null}
              {serverOptions.map((server) => (
                <option key={server} value={server}>
                  {server}
                </option>
              ))}
            </Input>
          </FormGroup>

          <FormGroup className="mb-3">
            <Label className="form-label mb-1">Período</Label>
            <Flatpickr
              className="form-control"
              value={parseMonthForPicker(periodo) ? [parseMonthForPicker(periodo)!] : []}
              options={{
                mode: "single",
                altInput: true,
                plugins: [
                  createMonthSelectPlugin({
                    shorthand: true,
                    dateFormat: "Y-m",
                    altFormat: "F Y",
                  }),
                ],
              }}
              onChange={(dates: Date[]) => {
                if (!dates || dates.length === 0 || !dates[0]) {
                  return;
                }
                const selected = dates[0];
                const value = `${selected.getFullYear()}-${String(
                  selected.getMonth() + 1,
                ).padStart(2, "0")}`;
                setPeriodo(value);
              }}
            />
          </FormGroup>

          <FormGroup className="mb-3">
            <Label className="form-label mb-1">Costo aplicado</Label>
            <Input
              type="select"
              value={modoCosto}
              onChange={(event) =>
                setModoCosto(event.target.value as "ultimo" | "ponderado")
              }
            >
              <option value="ponderado">Ponderado</option>
              <option value="ultimo">Último</option>
            </Input>
          </FormGroup>

          <FormGroup className="mb-3">
            <Label className="form-label mb-1">Estado</Label>
            <Input
              type="select"
              value={estadoFiltro}
              onChange={(event) =>
                setEstadoFiltro(event.target.value as "todos" | "activo" | "inactivo")
              }
            >
              <option value="todos">Todos</option>
              <option value="activo">Activo</option>
              <option value="inactivo">Inactivo</option>
            </Input>
          </FormGroup>

          <FormGroup className="mb-3">
            <Label className="form-label mb-1">Producción</Label>
            <Input
              type="select"
              value={produccionFiltro}
              onChange={(event) =>
                setProduccionFiltro(event.target.value as "todos" | "si" | "no")
              }
            >
              <option value="todos">Todos</option>
              <option value="si">Sí</option>
              <option value="no">No</option>
            </Input>
          </FormGroup>

          <Button
            color="dark"
            className="btn-soft-dark w-100"
            onClick={() => setFiltersOpen(false)}
          >
            Aplicar
          </Button>
        </OffcanvasBody>
      </Offcanvas>

      <Modal isOpen={detailsModalOpen} toggle={closeDetailsModal} size="xl" centered>
        <ModalHeader toggle={closeDetailsModal}>
          Composición de receta · {selectedRow?.descripcion ?? "Sin receta"}
        </ModalHeader>
        <ModalBody>
          {!selectedRow ? null : (
            <>
              <div className="d-flex flex-wrap align-items-center gap-2 mb-3">
                <small className="text-muted">
                  <strong>ID:</strong> {selectedRow.idReceta ?? "-"}
                </small>
                <small className="text-muted">
                  <strong>Nivel:</strong> {selectedRow.nivelIngrediente ?? "-"}
                </small>
                <span className="badge rounded-pill bg-warning-subtle text-warning">
                  Costo sin IVA: {costFormat.format(selectedRow.costoTotalSinIva ?? 0)}
                </span>
                <span className="badge rounded-pill bg-success-subtle text-success">
                  Costo con IVA: {costFormat.format(selectedRow.costoTotalConIva ?? 0)}
                </span>
                <small className="text-muted">
                  <strong>Estado:</strong> {selectedRow.esActiva ? "Activa" : "Inactiva"}
                </small>
              </div>

              <div className="table-responsive">
                <Table hover className="align-middle mb-0 table-nowrap">
                  <thead className="table-light">
                    <tr>
                      <th>Ingrediente</th>
                      <th className="text-end">Cantidad</th>
                      <th className="text-end">Costo U.</th>
                      <th>Mes de costo</th>
                      <th className="text-end">Subtotal</th>
                      <th className="text-center">ACC.</th>
                    </tr>
                  </thead>
                  <tbody>
                    {loadingDetails ? (
                      <tr>
                        <td colSpan={6} className="text-muted">
                          <Spinner size="sm" className="me-2" />
                          Cargando detalle...
                        </td>
                      </tr>
                    ) : detailRows.length === 0 ? (
                      <tr>
                        <td colSpan={6} className="text-muted">
                          Sin ingredientes disponibles para esta receta.
                        </td>
                      </tr>
                    ) : (
                      detailRows.map((item, index) => (
                        <tr key={`${item.ingrediente}-${index}`}>
                          <td>
                            <div className="d-flex align-items-center gap-2 text-nowrap">
                              <span className="badge bg-primary-subtle text-primary">
                                {item.idArticulo ?? "SIN ID"}
                              </span>
                              <span
                                className="text-truncate"
                                style={{ maxWidth: 520 }}
                                title={item.ingrediente}
                              >
                                {item.ingrediente}
                              </span>
                            </div>
                          </td>
                          <td className="text-end">
                            {quantityFormat.format(Number(item.cantidad ?? 0))}
                          </td>
                          <td className="text-end">
                            {costFormat.format(Number(item.costoUnitario ?? 0))}
                          </td>
                          <td>{item.mesCosto}</td>
                          <td className="text-end">
                            {costFormat.format(Number(item.subtotal ?? 0))}
                          </td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center">
                              <button
                                type="button"
                                className="btn btn-sm btn-soft-primary"
                                title={
                                  item.nivelIngrediente === 2
                                    ? "Ver subreceta"
                                    : "Ver compras"
                                }
                                disabled={!item.idArticulo}
                                onClick={() => openComponentModal(item)}
                              >
                                <i className="ri-search-eye-line"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                  <tfoot>
                    <tr className="table-light">
                      <th>Total</th>
                      <th className="text-end">
                        {detailRows.length > 0
                          ? quantityFormat.format(Number(detailTotalCantidad ?? 0))
                          : "-"}
                      </th>
                      <th className="text-end">
                        {detailRows.length > 0 && detailCostoUnitPromedioPonderado != null
                          ? costFormat.format(Number(detailCostoUnitPromedioPonderado ?? 0))
                          : "-"}
                      </th>
                      <th className="text-muted small">Prom. pond.</th>
                      <th className="text-end">
                        {costFormat.format(
                          detailRows.length > 0
                            ? Number(detailTotalConIva ?? 0)
                            : Number(selectedRow.costoTotalConIva ?? 0),
                        )}
                      </th>
                      <th className="text-center text-muted">-</th>
                    </tr>
                  </tfoot>
                </Table>
              </div>

              {detailsError ? (
                <Alert color="danger" className="mt-2 mb-0">
                  {detailsError}
                </Alert>
              ) : null}

              {selectedRow.ingredientesFaltantes ? (
                <div className="mt-2 text-muted small">
                  Ingredientes faltantes: {selectedRow.ingredientesFaltantes}
                </div>
              ) : null}

              <div className="d-flex justify-content-end mt-3">
                <Button color="dark" className="btn-soft-dark" onClick={closeDetailsModal}>
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      <Modal isOpen={componentModalOpen} toggle={closeComponentModal} size="xl" centered>
        <ModalHeader toggle={closeComponentModal}>
          {componentTarget?.tipo === "receta" ? "Detalle de subreceta" : "Detalle de compras"} ·{" "}
          {componentTarget?.ingrediente ?? "Sin componente"}
        </ModalHeader>
        <ModalBody>
          {!componentTarget ? null : (
            <>
              <div className="d-flex flex-wrap gap-2 mb-3 text-muted">
                <small>
                  <strong>ID:</strong> {componentTarget.idArticulo}
                </small>
                <small>
                  <strong>Tipo:</strong>{" "}
                  {componentTarget.tipo === "receta" ? "Subreceta" : "Compra"}
                </small>
                <small>
                  <strong>Período:</strong> {formatPeriodoMes(periodo)}
                </small>
                <small>
                  <strong>Servidor:</strong> {serverSeleccionado || "-"}
                </small>
                {componentTarget.tipo === "compra" ? (
                  <span className="badge rounded-pill bg-info-subtle text-info">
                    Mes de costo:{" "}
                    {componentCompraMesCosto
                      ? formatPeriodoMes(componentCompraMesCosto)
                      : formatPeriodoMes(periodo)}
                  </span>
                ) : null}
              </div>

              {componentError ? (
                <Alert color="danger">{componentError}</Alert>
              ) : null}

              {loadingComponent ? (
                <div className="text-muted py-2">
                  <Spinner size="sm" className="me-2" />
                  Cargando detalle...
                </div>
              ) : componentTarget.tipo === "receta" ? (
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0 table-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th>Ingrediente</th>
                        <th className="text-end">Cantidad</th>
                        <th className="text-end">Costo U.</th>
                        <th>Mes de costo</th>
                        <th className="text-end">Subtotal</th>
                        <th className="text-center">ACC.</th>
                      </tr>
                    </thead>
                    <tbody>
                      {componentRecetaRows.length === 0 ? (
                        <tr>
                          <td colSpan={6} className="text-muted">
                            Sin detalle disponible para esta subreceta.
                          </td>
                        </tr>
                      ) : (
                        componentRecetaRows.map((row, index) => (
                          <tr key={`${row.idArticulo ?? "sin-id"}-${index}`}>
                            <td>
                              <div className="d-flex align-items-center gap-2 text-nowrap">
                                <span className="badge bg-primary-subtle text-primary">
                                  {row.idArticulo ?? "SIN ID"}
                                </span>
                                <span
                                  className="text-truncate"
                                  style={{ maxWidth: 520 }}
                                  title={row.materiaPrima ?? "Sin ingrediente"}
                                >
                                  {row.materiaPrima ?? "Sin ingrediente"}
                                </span>
                              </div>
                            </td>
                            <td className="text-end">
                              {quantityFormat.format(Number(row.cantidad ?? 0))}
                            </td>
                            <td className="text-end">
                              {costFormat.format(Number(row.costoUnitConIva ?? 0))}
                            </td>
                            <td>{row.origenMesCosto || periodo}</td>
                            <td className="text-end">
                              {costFormat.format(Number(row.costoTotalConIva ?? 0))}
                            </td>
                            <td className="text-center">
                              <div className="d-flex justify-content-center">
                                <button
                                  type="button"
                                  className="btn btn-sm btn-soft-primary"
                                  title="Ver compras del ingrediente"
                                  disabled={!row.idArticulo}
                                  onClick={() =>
                                    openSubrecipeCompraModal(
                                      row.idArticulo,
                                      row.materiaPrima ?? null,
                                    )
                                  }
                                >
                                  <i className="ri-search-eye-line"></i>
                                </button>
                              </div>
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="table-light">
                        <th>Total</th>
                        <th className="text-end">
                          {componentRecetaRows.length > 0
                            ? quantityFormat.format(Number(componentRecetaTotalCantidad ?? 0))
                            : "-"}
                        </th>
                        <th className="text-end">
                          {componentRecetaRows.length > 0 &&
                          componentRecetaCostoUnitPromedioPonderado != null
                            ? costFormat.format(
                                Number(componentRecetaCostoUnitPromedioPonderado ?? 0),
                              )
                            : "-"}
                        </th>
                        <th className="text-muted small">Prom. pond.</th>
                        <th className="text-end">
                          {componentRecetaRows.length > 0
                            ? costFormat.format(Number(componentRecetaTotalConIva ?? 0))
                            : "-"}
                        </th>
                        <th className="text-center text-muted">-</th>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0 table-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th>Fecha</th>
                        <th>Factura</th>
                        <th className="text-end">Cantidad</th>
                        <th className="text-end">Costo U.</th>
                        <th className="text-end">Importe</th>
                        <th>Depósito</th>
                        <th>Proveedor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {componentCompraRows.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-muted">
                            Sin compras históricas para este artículo.
                          </td>
                        </tr>
                      ) : (
                        componentCompraRows.map((row, index) => (
                          <tr key={`${row.fecha ?? "sin-fecha"}-${row.factura ?? "sin-fac"}-${index}`}>
                            <td>{row.fecha ?? "-"}</td>
                            <td>{row.nroFactura || (row.factura != null ? String(row.factura) : "-")}</td>
                            <td className="text-end">
                              {quantityFormat.format(Number(row.cantidad ?? 0))}
                            </td>
                            <td className="text-end">
                              {costFormat.format(Number(row.costoUnitConIva ?? 0))}
                            </td>
                            <td className="text-end">
                              {costFormat.format(Number(row.importeTotal ?? 0))}
                            </td>
                            <td>{row.deposito ?? "-"}</td>
                            <td>{row.persona ?? "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="table-light">
                        <th colSpan={2}>Total</th>
                        <th className="text-end">
                          {componentCompraRows.length > 0
                            ? quantityFormat.format(Number(componentCompraTotalCantidad ?? 0))
                            : "-"}
                        </th>
                        <th className="text-end">
                          {componentCompraRows.length > 0 &&
                          componentCompraCostoUnitPromedioPonderado != null
                            ? costFormat.format(
                                Number(componentCompraCostoUnitPromedioPonderado ?? 0),
                              )
                            : "-"}
                        </th>
                        <th className="text-end">
                          {componentCompraRows.length > 0
                            ? costFormat.format(Number(componentCompraTotalImporte ?? 0))
                            : "-"}
                        </th>
                        <th colSpan={2} className="text-muted small">
                          Costo U. prom. pond.
                        </th>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              )}

              <div className="d-flex justify-content-end mt-3">
                <Button color="dark" className="btn-soft-dark" onClick={closeComponentModal}>
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>

      <Modal
        isOpen={subrecipeCompraModalOpen}
        toggle={closeSubrecipeCompraModal}
        size="xl"
        centered
      >
        <ModalHeader toggle={closeSubrecipeCompraModal}>
          Compras del ingrediente · {subrecipeCompraTarget?.ingrediente ?? "Sin ingrediente"}
        </ModalHeader>
        <ModalBody>
          {!subrecipeCompraTarget ? null : (
            <>
              <div className="d-flex flex-wrap gap-2 mb-3 text-muted">
                <small>
                  <strong>ID:</strong> {subrecipeCompraTarget.idArticulo}
                </small>
                <small>
                  <strong>Período:</strong> {formatPeriodoMes(periodo)}
                </small>
                <small>
                  <strong>Servidor:</strong> {serverSeleccionado || "-"}
                </small>
                <span className="badge rounded-pill bg-info-subtle text-info">
                  Mes de costo:{" "}
                  {subrecipeCompraMesCosto
                    ? formatPeriodoMes(subrecipeCompraMesCosto)
                    : formatPeriodoMes(periodo)}
                </span>
              </div>

              {subrecipeCompraError ? (
                <Alert color="danger">{subrecipeCompraError}</Alert>
              ) : null}

              {loadingSubrecipeCompra ? (
                <div className="text-muted py-2">
                  <Spinner size="sm" className="me-2" />
                  Cargando compras...
                </div>
              ) : (
                <div className="table-responsive">
                  <Table hover className="align-middle mb-0 table-nowrap">
                    <thead className="table-light">
                      <tr>
                        <th>Fecha</th>
                        <th>Factura</th>
                        <th className="text-end">Cantidad</th>
                        <th className="text-end">Costo U.</th>
                        <th className="text-end">Importe</th>
                        <th>Depósito</th>
                        <th>Proveedor</th>
                      </tr>
                    </thead>
                    <tbody>
                      {subrecipeCompraRows.length === 0 ? (
                        <tr>
                          <td colSpan={7} className="text-muted">
                            Sin compras históricas para este ingrediente.
                          </td>
                        </tr>
                      ) : (
                        subrecipeCompraRows.map((row, index) => (
                          <tr
                            key={`${row.fecha ?? "sin-fecha"}-${row.factura ?? "sin-fac"}-${index}`}
                          >
                            <td>{row.fecha ?? "-"}</td>
                            <td>{row.nroFactura || (row.factura != null ? String(row.factura) : "-")}</td>
                            <td className="text-end">
                              {quantityFormat.format(Number(row.cantidad ?? 0))}
                            </td>
                            <td className="text-end">
                              {costFormat.format(Number(row.costoUnitConIva ?? 0))}
                            </td>
                            <td className="text-end">
                              {costFormat.format(Number(row.importeTotal ?? 0))}
                            </td>
                            <td>{row.deposito ?? "-"}</td>
                            <td>{row.persona ?? "-"}</td>
                          </tr>
                        ))
                      )}
                    </tbody>
                    <tfoot>
                      <tr className="table-light">
                        <th colSpan={2}>Total</th>
                        <th className="text-end">
                          {subrecipeCompraRows.length > 0
                            ? quantityFormat.format(Number(subrecipeCompraTotalCantidad ?? 0))
                            : "-"}
                        </th>
                        <th className="text-end">
                          {subrecipeCompraRows.length > 0 &&
                          subrecipeCompraCostoUnitPromedioPonderado != null
                            ? costFormat.format(
                                Number(subrecipeCompraCostoUnitPromedioPonderado ?? 0),
                              )
                            : "-"}
                        </th>
                        <th className="text-end">
                          {subrecipeCompraRows.length > 0
                            ? costFormat.format(Number(subrecipeCompraTotalImporte ?? 0))
                            : "-"}
                        </th>
                        <th colSpan={2} className="text-muted small">
                          Costo U. prom. pond.
                        </th>
                      </tr>
                    </tfoot>
                  </Table>
                </div>
              )}

              <div className="d-flex justify-content-end mt-3">
                <Button
                  color="dark"
                  className="btn-soft-dark"
                  onClick={closeSubrecipeCompraModal}
                >
                  Cerrar
                </Button>
              </div>
            </>
          )}
        </ModalBody>
      </Modal>
    </div>
  );
};

export default StockRecetasOfflinePage;
